// ═══════════════════════════════════════════════════════════════════════════
// Daily / Weekly quest pool + rollover logic
// ═══════════════════════════════════════════════════════════════════════════
// On UTC midnight rollover, three random dailies are picked from
// `DAILY_QUEST_POOL`. On Monday UTC midnight, three random weeklies are
// picked from `WEEKLY_QUEST_POOL`. Faction-specific quests bake in a fixed
// faction at pool-definition time so the same quest id always means the
// same goal (no fragile lookup at claim time).
//
// Reward shape: dailies pay 20-30 runes each, weeklies pay 80-100. Gold and
// XP fields stay 0 — quests are now a pure rune-economy hook.
// ═══════════════════════════════════════════════════════════════════════════
// ── Daily pool ──────────────────────────────────────────────────────────────
// 14 entries. Three picked randomly per UTC-midnight rollover.
// Numbers tuned against actual run data: a 100-stage run lands ~5-10k kills
// and several thousand crits, so a daily target of 1k crits or 2k kills is
// comfortably hit in 1-2 focused runs without being a chore.
export const DAILY_QUEST_POOL = [
    // ── Kill counts ──
    { id: 'daily_slay_2k', kind: 'kill', name: 'Slayer', description: 'Kill 2,000 enemies in any zone', targetAmount: 2000, runes: 25 },
    { id: 'daily_slay_5k', kind: 'kill', name: 'Massacre', description: 'Kill 5,000 enemies in any zone', targetAmount: 5000, runes: 30 },
    { id: 'daily_kill_boss_5', kind: 'kill_boss', name: 'Boss Hunter', description: 'Defeat 5 zone bosses', targetAmount: 5, runes: 30 },
    // ── Faction quests ──
    { id: 'daily_kill_undead_500', kind: 'kill_faction', faction: 'undead', name: 'Undead Cleansing', description: 'Kill 500 undead enemies', targetAmount: 500, runes: 25 },
    { id: 'daily_kill_orc_500', kind: 'kill_faction', faction: 'orc', name: 'Orc Purge', description: 'Kill 500 orcs', targetAmount: 500, runes: 25 },
    { id: 'daily_kill_demon_500', kind: 'kill_faction', faction: 'demon', name: 'Banishment', description: 'Kill 500 demons', targetAmount: 500, runes: 25 },
    { id: 'daily_kill_goblin_500', kind: 'kill_faction', faction: 'goblin', name: 'Goblin Hunt', description: 'Kill 500 goblins', targetAmount: 500, runes: 25 },
    // ── Stage gates ──
    { id: 'daily_reach_20', kind: 'reach_stage', name: 'Stage 20', description: 'Reach Stage 20 in a single run', targetAmount: 20, runes: 20 },
    { id: 'daily_reach_40', kind: 'reach_stage', name: 'Deeper Still', description: 'Reach Stage 40 in a single run', targetAmount: 40, runes: 30, requiredLevel: 5 },
    // ── Combat milestones ──
    { id: 'daily_crits_1000', kind: 'crit', name: 'Critical Striker', description: 'Land 1,000 critical hits', targetAmount: 1000, runes: 25 },
    { id: 'daily_potions_10', kind: 'potion', name: 'Battle Mixer', description: 'Use 10 potions from the consumable bar', targetAmount: 10, runes: 20 },
];
// ── Weekly pool ─────────────────────────────────────────────────────────────
// 8 entries. Three picked randomly per Monday-UTC rollover.
export const WEEKLY_QUEST_POOL = [
    { id: 'weekly_slay_50k', kind: 'kill', name: 'Weekly Massacre', description: 'Kill 50,000 enemies this week', targetAmount: 50000, runes: 100 },
    { id: 'weekly_kill_boss_100', kind: 'kill_boss', name: 'Boss Hunter Supreme', description: 'Defeat 100 zone bosses', targetAmount: 100, runes: 100 },
    { id: 'weekly_reach_60', kind: 'reach_stage', name: 'Deep Dive', description: 'Reach Stage 60 in a single run', targetAmount: 60, runes: 100, requiredLevel: 10 },
    // weekly_inferno_clear — pulled until tracking ships in Phase 2 (no event
    // hook today for "completed an inferno run", and pool entries that can't
    // progress are dead weight in the rotation).
    { id: 'weekly_dailies_10', kind: 'dailies', name: 'Diligent', description: 'Complete 10 daily quests', targetAmount: 10, runes: 100 },
    { id: 'weekly_kill_undead_5k', kind: 'kill_faction', faction: 'undead', name: 'Crypt Cleansing', description: 'Kill 5,000 undead', targetAmount: 5000, runes: 80 },
    { id: 'weekly_kill_orc_5k', kind: 'kill_faction', faction: 'orc', name: 'Orc Pogrom', description: 'Kill 5,000 orcs', targetAmount: 5000, runes: 80 },
    { id: 'weekly_kill_demon_5k', kind: 'kill_faction', faction: 'demon', name: 'Demon Purge', description: 'Kill 5,000 demons', targetAmount: 5000, runes: 80 },
];
// ── Rollover math ───────────────────────────────────────────────────────────
/** Returns the unix-ms timestamp of the most recent UTC midnight on or
 *  before `now`. A daily reset is due if `lastDailyReset < this`. */
export function lastDailyResetBefore(now) {
    const d = new Date(now);
    d.setUTCHours(0, 0, 0, 0);
    return d.getTime();
}
/** Returns the unix-ms timestamp of the most recent Monday UTC midnight on
 *  or before `now`. ISO weeks start on Monday (day 1); Sunday is day 0 in
 *  `getUTCDay()` so we map 0 → 6 to make the math consistent. */
export function lastWeeklyResetBefore(now) {
    const d = new Date(now);
    d.setUTCHours(0, 0, 0, 0);
    const day = d.getUTCDay(); // 0=Sun..6=Sat
    const daysSinceMonday = (day + 6) % 7;
    d.setUTCDate(d.getUTCDate() - daysSinceMonday);
    return d.getTime();
}
/** True if a daily roll is due (new UTC day). */
export function shouldRollDaily(lastReset, now) {
    if (lastReset === undefined)
        return true;
    return lastReset < lastDailyResetBefore(now);
}
/** True if a weekly roll is due (new ISO week). */
export function shouldRollWeekly(lastReset, now) {
    if (lastReset === undefined)
        return true;
    return lastReset < lastWeeklyResetBefore(now);
}
// ── Roll helpers ────────────────────────────────────────────────────────────
function pickRandomN(pool, n, rng = Math.random) {
    const copy = [...pool];
    const out = [];
    for (let i = 0; i < n && copy.length > 0; i++) {
        const idx = Math.floor(rng() * copy.length);
        out.push(copy.splice(idx, 1)[0]);
    }
    return out;
}
function entryToQuest(entry, type) {
    return {
        id: entry.id,
        name: entry.name,
        description: entry.description,
        type,
        status: 'available',
        targetAmount: entry.targetAmount,
        currentAmount: 0,
        requiredLevel: entry.requiredLevel ?? 1,
        rewards: { gold: 0, xp: 0, loot: entry.runes },
        faction: entry.faction,
    };
}
/** Pick three random daily quests from the pool. */
export function rollDailyQuests(rng = Math.random) {
    return pickRandomN(DAILY_QUEST_POOL, 3, rng).map((e) => entryToQuest(e, 'daily'));
}
/** Pick three random weekly quests from the pool. */
export function rollWeeklyQuests(rng = Math.random) {
    return pickRandomN(WEEKLY_QUEST_POOL, 3, rng).map((e) => entryToQuest(e, 'weekly'));
}
/** Lookup a pool entry by id (used by GameStateManager to dispatch tracking
 *  to the right `kind` without storing the kind on the Quest itself — the
 *  Quest type stays compatible with old saves that didn't have it). */
const ALL_POOL_ENTRIES = (() => {
    const map = {};
    for (const e of DAILY_QUEST_POOL)
        map[e.id] = e;
    for (const e of WEEKLY_QUEST_POOL)
        map[e.id] = e;
    return map;
})();
export function getQuestPoolEntry(questId) {
    return ALL_POOL_ENTRIES[questId];
}
//# sourceMappingURL=dailyQuests.js.map